import { ArrowLeft, CheckCircle, ArrowRight, Shield, Target, Users, Award, TrendingUp, Lock, Globe, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from "framer-motion";
import { useEffect } from 'react';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from "@/components/ui/card";
import SEO from '@/components/SEO';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  
  return (
    <PageLayout>
      <SEO 
        title="About CIPHER Security | Cybersecurity Excellence"
        description="Learn about CIPHER Security's boutique cybersecurity approach. Expert subject matter specialists delivering focused excellence in cyber risk management."
      />
      
      {/* Hero Section */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        {/* Animated background gradient */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 30%, rgba(0, 168, 107, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 70%, rgba(6, 182, 212, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(251, 191, 36, 0.1) 0%, transparent 70%)" }} />
        </div>

        <div className="container mx-auto px-4 sm:px-8 relative z-10" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center mb-8 transition-colors group rounded-full px-5 py-2 font-semibold shadow-lg"
              style={{
                background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: "999px",
                boxShadow: "0 4px 24px 0 rgba(0, 168, 107, 0.18)"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h1
                className="mb-6"
                style={{
                  fontSize: "clamp(2rem, 6vw, 3.75rem)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "#fff",
                  letterSpacing: "-0.025em",
                }}
              >
                <span
                  className="block"
                  style={{
                    background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "#00a86b",
                    fontWeight: 700,
                  }}
                >
                  Cybersecurity Excellence
                </span>
                <span className="block mt-2 text-base sm:text-lg md:text-2xl" style={{ color: "#e5e7eb" }}>
                  Boutique Expertise, Focused Results
                </span>
              </h1>
              <p
                className="max-w-3xl mx-auto text-base sm:text-lg px-4 sm:px-0"
                style={{
                  color: "#d1d5db",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                We're a small boutique cybersecurity company. We don't do everything in cyber, but what we do, we do exceptionally well. A band of cybersecurity subject matter experts delivering quality cyber risk management professional services.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section
        className="py-16 px-4 sm:px-8"
        style={{
          background: "#0f0f17",
        }}
      >
        <div className="container mx-auto" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            {/* Mission & Vision */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >
                <Card
                  className="h-full group relative overflow-hidden"
                  style={{
                    background: "rgba(6, 182, 212, 0.08)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(6, 182, 212, 0.30)",
                    borderRadius: 16,
                    padding: 0,
                    transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                    transformStyle: "preserve-3d",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(6, 182, 212, 0.15)";
                    e.currentTarget.style.borderColor = "#06b6d4";
                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(6, 182, 212, 0.20)";
                    e.currentTarget.style.transform = "translateY(-8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(6, 182, 212, 0.08)";
                    e.currentTarget.style.borderColor = "rgba(6, 182, 212, 0.30)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0px)";
                  }}
                >
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row items-start mb-6">
                      <div
                        className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-4"
                        style={{
                          width: 64,
                          height: 64,
                          padding: 16,
                          borderRadius: 12,
                          backgroundColor: "#06b6d420",
                          color: "#06b6d4",
                          marginBottom: 24,
                          boxShadow: "0 4px 16px #06b6d420",
                          transition: "transform 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.1)";
                          e.currentTarget.style.filter = "drop-shadow(0 0 20px #06b6d4)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.filter = "none";
                        }}
                      >
                        <Target className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl sm:text-2xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          What CIPHER Means
                        </h3>
                      </div>
                    </div>
                    <p
                      className="text-sm sm:text-base"
                      style={{
                        color: "#d1d5db",
                        lineHeight: 1.6,
                        marginBottom: 16,
                      }}
                    >
                      CIPHER represents our specialized focus:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-6">
                      {[
                        "Cyber Information Protection",
                        "Infrastructure Security", 
                        "Preventing Cyber Incidents",
                        "Hack Exploitation Assessment",
                        "Expertise in Cyber Risk Management",
                        "Reconnaissance & Response"
                      ].map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-start text-xs sm:text-sm"
                          style={{
                            color: "#9ca3af",
                          }}
                        >
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              background: "#06b6d4",
                              borderRadius: "50%",
                              marginRight: 8,
                              marginTop: 6,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ lineHeight: 1.4 }}>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.1,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >
                <Card
                  className="h-full group relative overflow-hidden"
                  style={{
                    background: "rgba(168, 85, 247, 0.08)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(168, 85, 247, 0.30)",
                    borderRadius: 16,
                    padding: 0,
                    transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                    transformStyle: "preserve-3d",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(168, 85, 247, 0.15)";
                    e.currentTarget.style.borderColor = "#a855f7";
                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(168, 85, 247, 0.20)";
                    e.currentTarget.style.transform = "translateY(-8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(168, 85, 247, 0.08)";
                    e.currentTarget.style.borderColor = "rgba(168, 85, 247, 0.30)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0px)";
                  }}
                >
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row items-start mb-6">
                      <div
                        className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-4"
                        style={{
                          width: 64,
                          height: 64,
                          padding: 16,
                          borderRadius: 12,
                          backgroundColor: "#a855f720",
                          color: "#a855f7",
                          marginBottom: 24,
                          boxShadow: "0 4px 16px #a855f720",
                          transition: "transform 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.1)";
                          e.currentTarget.style.filter = "drop-shadow(0 0 20px #a855f7)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.filter = "none";
                        }}
                      >
                        <Award className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl sm:text-2xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          Our Approach
                        </h3>
                      </div>
                    </div>
                    <p
                      className="text-sm sm:text-base"
                      style={{
                        color: "#d1d5db",
                        lineHeight: 1.6,
                        marginBottom: 16,
                      }}
                    >
                      Our comprehensive cybersecurity methodology encompasses:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-6">
                      {[
                        "Prevent & Protect",
                        "Detect & Monitor", 
                        "Respond & Recover",
                        "Assess & Analyze",
                        "Secure & Control",
                        "Improve & Optimize"
                      ].map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center text-xs sm:text-sm"
                          style={{
                            color: "#9ca3af",
                          }}
                        >
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              background: "#a855f7",
                              borderRadius: "50%",
                              marginRight: 8,
                              flexShrink: 0,
                            }}
                          />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Our Story */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-16"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <h2
                  className="text-2xl sm:text-3xl md:text-4xl"
                  style={{
                    fontWeight: 700,
                    color: "#fff",
                    letterSpacing: "-0.01em",
                    marginBottom: 16,
                  }}
                >
                  Why Work With Us
                </h2>
                <p
                  className="text-base sm:text-lg px-4 sm:px-0"
                  style={{
                    color: "#d1d5db",
                    maxWidth: 800,
                    margin: "0 auto",
                  }}
                >
                  Small enough to care, experienced enough to deliver
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.2,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >
                <Card
                  className="group relative overflow-hidden"
                  style={{
                    background: "rgba(59, 130, 246, 0.08)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(59, 130, 246, 0.30)",
                    borderRadius: 16,
                    padding: 0,
                    transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                    transformStyle: "preserve-3d",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(59, 130, 246, 0.15)";
                    e.currentTarget.style.borderColor = "#3b82f6";
                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(59, 130, 246, 0.20)";
                    e.currentTarget.style.transform = "translateY(-8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(59, 130, 246, 0.08)";
                    e.currentTarget.style.borderColor = "rgba(59, 130, 246, 0.30)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0px)";
                  }}
                >
                  <CardContent className="p-6 sm:p-8">
                    <div className="space-y-6">
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.6,
                            marginBottom: 16,
                          }}
                        >
                          As a boutique cybersecurity firm, we focus on delivering cybersecurity risk management excellence rather than trying to be everything to everyone. Our selective approach means we excel in the areas that matter most to our clients' security posture.
                        </p>
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.6,
                            marginBottom: 16,
                          }}
                        >
                          Our band of cybersecurity subject matter experts brings deep specialization in cyber information protection, infrastructure security, and hack exploitation reconnaissance. We believe that true cybersecurity excellence comes from focused expertise, not broad generalization.
                        </p>
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.6,
                            marginBottom: 16,
                          }}
                        >
                          Every client engagement receives the direct attention of senior cybersecurity professionals. When you work with team CIPHER, you work with the experts who have built their careers on delivering measurable, quality cybersecurity risk management results.
                        </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>

            {/* Call to Action - COMMENTED OUT */}
            {/* 
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.3,
                ease: [0.16, 1, 0.3, 1]
              }}
            >
              <Card
                className="text-center group relative overflow-hidden"
                style={{
                  background: "rgba(251, 191, 36, 0.08)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(251, 191, 36, 0.30)",
                  borderRadius: 16,
                  padding: 0,
                  transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                  transformStyle: "preserve-3d",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(251, 191, 36, 0.15)";
                  e.currentTarget.style.borderColor = "#fbbf24";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(251, 191, 36, 0.20)";
                  e.currentTarget.style.transform = "translateY(-8px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(251, 191, 36, 0.08)";
                  e.currentTarget.style.borderColor = "rgba(251, 191, 36, 0.30)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0px)";
                }}
              >
                <CardContent style={{ padding: 32 }}>
                  <div
                    className="flex items-center justify-center mx-auto mb-6"
                    style={{
                      width: 64,
                      height: 64,
                      padding: 16,
                      borderRadius: 12,
                      backgroundColor: "#fbbf2420",
                      color: "#fbbf24",
                      boxShadow: "0 4px 16px #fbbf2420",
                      transition: "transform 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.1)";
                      e.currentTarget.style.filter = "drop-shadow(0 0 20px #fbbf24)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.filter = "none";
                    }}
                  >
                    <Globe className="w-6 h-6" />
                  </div>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#fff",
                      marginBottom: 8,
                    }}
                  >
                    Ready to Secure Your Future?
                  </h3>
                  <p
                    style={{
                      color: "#d1d5db",
                      fontSize: 16,
                      lineHeight: 1.6,
                      marginBottom: 16,
                    }}
                  >
                    Join the organizations that trust CIPHER Security to protect their digital transformation journey.
                    Let's build a more secure digital world together.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
                    <Link
                      to="/careers"
                      className="inline-flex items-center font-medium transition-all group-hover:translate-x-1"
                      style={{
                        color: "#fbbf24",
                        fontSize: 16,
                        fontWeight: 600,
                      }}
                    >
                      Join Our Team
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                    <Link
                      to="/contactus"
                      className="inline-flex items-center font-medium transition-all group-hover:translate-x-1"
                      style={{
                        color: "#fbbf24",
                        fontSize: 16,
                        fontWeight: 600,
                      }}
                    >
                      Get Started
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            */}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default About;